import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import { useGetFilterOptions } from '@/api/queries/filter/useGetFilterOptions';
import { getSelectComponentByType } from '@/components/filter/Filter/componentsMapping';
import { getIsDisabled, getIsHidden } from '@/components/filter/Filter/utils';
import AdobeAnalyticsContext from '@/context/AdobeAnalyticsContext';
import { useAuthContext } from '@/context/AuthContext';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { FILTER_CONFIG, FilterType } from '@/models';
import { collectOptionPathsForSelection } from '@/models/filter/utils/collectOptionPathsForSelection';
import { DropdownPosition } from '@/storybook/components/FormCollection/utils/utils';
import WithLabel from '@/storybook/components/WithLabel/WithLabel';
import { isDynamicFilter } from '@/types/filter';
import { translateDynamicKey } from '@/types/translation';
import { trackSiteSearch } from '@/utils/analytics/trackOnSiteSearch';
import {
  buildAvailableValueSet,
  buildOrphanedSelectionOptions,
  toSelectableOptions,
  valueToAvailabilityKey,
} from '@/utils/filter/cascadingFilters';
import cx from 'classnames';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Control, Controller, ControllerRenderProps, Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styles from './Filter.module.scss';
import { useRecordLocalOptionPaths } from './useRecordLocalOptionPaths';

const EMPTY_OPTIONS: never[] = [];
const CASCADING_FILTER_TYPES: ReadonlySet<string> = new Set(['multiselect', 'treeselect']);

const useRegisterAvailability = (
  filterKey: FilterType,
  availableValueSet: Set<string>,
  shouldRegister: boolean,
  resolvedOptions: readonly unknown[] | undefined,
) => {
  const { registerAvailableValues } = useFiltersContext();

  useEffect(() => {
    if (!shouldRegister || resolvedOptions === undefined || !registerAvailableValues) return;
    registerAvailableValues(filterKey, availableValueSet);
  }, [shouldRegister, resolvedOptions, filterKey, availableValueSet, registerAvailableValues]);
};

interface FilterSelectProps<T extends Record<string, unknown>> {
  filterKey: FilterType;
  field: ControllerRenderProps<T, Path<T>>;
  disabled?: boolean;
  label?: string;
  hideLabel?: boolean;
  isSecondaryLevelFilter?: boolean;
  onDropdownToggle?: (isOpen: boolean) => void;
  useFloatingLabelProp?: boolean;
  icon?: React.ReactNode;
  dropdownPosition?: DropdownPosition;
}

export const FilterSelect = <T extends Record<string, unknown>>({
  filterKey,
  field,
  disabled,
  hideLabel,
  isSecondaryLevelFilter,
  useFloatingLabelProp,
  ...rest
}: FilterSelectProps<T>) => {
  const { t } = useTranslation();
  let location = useLocation();

  const config = FILTER_CONFIG[filterKey];
  const formContext = useFormContext<T>();
  const { userInfo } = useAuthContext();
  const { data: userCountries } = useGetUserCountries();
  const { stageOptionPaths } = useFiltersContext();

  const isCascadingFilter = isDynamicFilter(config) && CASCADING_FILTER_TYPES.has(config.type);
  const usesOptionPaths = isDynamicFilter(config) && config.usesOptionPaths === true;
  const recordsOptionPaths = isDynamicFilter(config) && config.recordsOptionPaths === true;
  const isSecondaryFilter = isSecondaryLevelFilter === true;
  const selectedFilterValues = useMemo<unknown[]>(() => (Array.isArray(field.value) ? field.value : []), [field.value]);

  const { data: filteredOptionsData, isLoading: isLoadingFilteredOptions } = useGetFilterOptions({
    type: filterKey,
    applyDynamicFilters: config.applyDynamicFilters,
    enabled: !isSecondaryFilter,
  });
  const options = filteredOptionsData ?? EMPTY_OPTIONS;
  const { data: unfilteredOptions = [], isLoading: isLoadingUnfilteredOptions } = useGetFilterOptions({
    type: filterKey,
    applyDynamicFilters: config.applyDynamicFilters,
    isSecondarySegment: isSecondaryFilter,
    enabled: isSecondaryFilter,
  });

  const availableValueSet = useMemo(() => buildAvailableValueSet(options), [options]);
  const hasUnavailableSelectedValues = useMemo(
    () => selectedFilterValues.some(selectedValue => !availableValueSet.has(valueToAvailabilityKey(selectedValue))),
    [availableValueSet, selectedFilterValues],
  );
  const shouldLoadFullUniverseOptions =
    isCascadingFilter && !isSecondaryFilter && !isLoadingFilteredOptions && hasUnavailableSelectedValues;

  // Cascading filters render the full universe of options; fetch it with the
  // other active filters removed only when a selected value is unavailable.
  // `options` (dynamically filtered) becomes the "available" set that drives
  // enabled vs. disabled.
  const { data: fullUniverseOptions = [], isLoading: isLoadingFullUniverseOptions } = useGetFilterOptions({
    type: filterKey,
    applyDynamicFilters: false,
    enabled: shouldLoadFullUniverseOptions,
  });

  const isLoading = isSecondaryFilter
    ? isLoadingUnfilteredOptions
    : isLoadingFilteredOptions || (shouldLoadFullUniverseOptions && isLoadingFullUniverseOptions);

  useRegisterAvailability(filterKey, availableValueSet, isCascadingFilter && !isSecondaryFilter, filteredOptionsData);

  // Record the option paths this selection is being made against so `applyFilter`
  // can rebuild the identical query after a refresh, when the option query that
  // produced them no longer runs. Reduced to what the builder reads, because the
  // registry is persisted. Staged rather than registered: this is the drawer's
  // draft value, and only Apply promotes it into the registry.
  const optionPathsForSelection = useMemo(() => {
    // An in-flight or failed option query yields an empty universe; staging it
    // would describe the selection against paths this filter never offered. The
    // check is hoisted so the empty-selection case below cannot bypass it.
    if (!usesOptionPaths || options.length === 0) return null;
    const selectedPaths = selectedFilterValues.filter(
      (selectedValue): selectedValue is string => typeof selectedValue === 'string',
    );
    if (selectedPaths.length === 0) return [];
    const allPaths: string[] = [];
    for (const option of options) {
      if (typeof option.value === 'string') allPaths.push(option.value);
    }
    if (allPaths.length === 0) return null;
    return collectOptionPathsForSelection(allPaths, selectedPaths);
  }, [usesOptionPaths, options, selectedFilterValues]);

  useEffect(() => {
    if (!optionPathsForSelection || isSecondaryLevelFilter || !stageOptionPaths) return;
    stageOptionPaths(filterKey, optionPathsForSelection);
  }, [isSecondaryLevelFilter, filterKey, optionPathsForSelection, stageOptionPaths]);

  useRecordLocalOptionPaths(filterKey, options, recordsOptionPaths && !isSecondaryFilter);

  // The option array identity drives every downstream list/tree memo (nested
  // tree build, sort, descendant map). Selecting a value must not change it, so
  // the selectable options are derived from `options` alone and the orphaned
  // selections are appended only when there actually are any.
  const selectableOptions = useMemo(() => toSelectableOptions(options), [options]);

  const orphanedOptions = useMemo(() => {
    if (!isCascadingFilter) return [];
    return buildOrphanedSelectionOptions(options, fullUniverseOptions, selectedFilterValues);
  }, [isCascadingFilter, options, fullUniverseOptions, selectedFilterValues]);

  const cascadingOptions = useMemo(() => {
    if (!isCascadingFilter) return undefined;
    return orphanedOptions.length ? [...selectableOptions, ...orphanedOptions] : selectableOptions;
  }, [isCascadingFilter, selectableOptions, orphanedOptions]);

  const isDisabled =
    disabled ??
    getIsDisabled(config.disabled, options, {
      userInfo,
      location,
      isSecondaryLevelFilter,
    });

  const { placeholder, getTooltip } = config;
  const placeholderLabel = useMemo(() => {
    if (typeof placeholder === 'function') {
      return userInfo ? translateDynamicKey(t, placeholder({ userInfo, userCountries })) : '';
    }
    return translateDynamicKey(t, placeholder ?? '');
  }, [userInfo, userCountries, t, placeholder]);

  const tooltip = useMemo(() => {
    if (!getTooltip || !userInfo) return null;
    return getTooltip({ userInfo });
  }, [userInfo, getTooltip]);

  const analytics = useContext(AdobeAnalyticsContext);

  const handleInputChange = useCallback(
    (value: string) => {
      const searchPos = translateDynamicKey(t, config.label);
      trackSiteSearch({
        value,
        trackOnSiteSearch: analytics?.trackOnSiteSearch,
        searchPos,
      });
      field.onChange(value);
    },
    [analytics, config.label, t, field],
  );

  const handleOnClear = useCallback(() => {
    field.onChange(config.defaultValue);
    formContext.trigger(field.name);
  }, [formContext, field, config.defaultValue]);

  const baseOptions = useMemo(() => {
    const isArrayBasedType = config.type === 'multiselect' || config.type === 'multilevelselect';
    const fieldValue: unknown =
      isArrayBasedType && (field.value === null || field.value === undefined) ? [] : field.value;

    const resolvedOptions = isSecondaryLevelFilter ? unfilteredOptions : (cascadingOptions ?? options);

    return {
      options: resolvedOptions,
      unfilteredOptions,
      isLoading,
      value: fieldValue,
      onChange: handleInputChange,
      placeholder: placeholderLabel,
      disabled: isDisabled,
      clearable: config.clearable ?? false,
      onClear: config.clearable ? handleOnClear : undefined,
      searchable: config.searchable ?? false,
      comingSoon: config.comingSoon ?? false,
      label: config.type === 'checkbox' ? translateDynamicKey(t, config.label) : '',
      ...(config.type === 'treeselect' && { selectionMode: config.selectionMode }),
      ...((config.type === 'treeselect' || config.type === 'oldtreeselect') && {
        collapseSingleChild: config.collapseSingleChild,
      }),
    };
  }, [
    config.type,
    field.value,
    isSecondaryLevelFilter,
    unfilteredOptions,
    options,
    cascadingOptions,
    isLoading,
    handleInputChange,
    handleOnClear,
    placeholderLabel,
    isDisabled,
    config.clearable,
    config.searchable,
    config.comingSoon,
    config.label,
    t,
    config.selectionMode,
    config.collapseSingleChild,
  ]);

  const FilterUIComponent = useMemo(
    () => getSelectComponentByType(config.type, useFloatingLabelProp ?? config.useFloatingLabel ?? false),
    [config.type, useFloatingLabelProp, config.useFloatingLabel],
  );

  const isHidden = getIsHidden(config.hidden, options, { userInfo, isSecondaryLevelFilter });

  if (isHidden) return null;

  return (
    <WithLabel
      tooltip={tooltip}
      label={translateDynamicKey(t, config.label)}
      className={cx({
        [styles.filterContainer]: true,
        [styles.hideLabel]: config.type === 'checkbox' || hideLabel,
      })}
    >
      {FilterUIComponent && <FilterUIComponent {...baseOptions} {...rest} />}
    </WithLabel>
  );
};

interface FilterProps<T extends Record<string, unknown>> {
  filterKey: FilterType;
  control: Control<T>;
  name: Path<T>;
  disabled?: boolean;
  hideLabel?: boolean;
  isSecondaryLevelFilter?: boolean;
  classNames?: string;
  onDropdownToggle?: (isOpen: boolean) => void;
  useFloatingLabel?: boolean;
  icon?: React.ReactNode;
  dropdownPosition?: DropdownPosition;
}

const Filter = <T extends Record<string, unknown>>({
  filterKey,
  name,
  control,
  classNames,
  useFloatingLabel,
  isSecondaryLevelFilter,
  ...rest
}: FilterProps<T>) => {
  const config = FILTER_CONFIG[filterKey];
  const { t } = useTranslation();
  const analytics = useContext(AdobeAnalyticsContext);

  const handleOneDataLayer = useCallback(() => {
    analytics?.trackLinkClick({
      innertext: translateDynamicKey(t, config?.label),
      type: AdobeAnalyticsConst.LINK_TYPES.BUTTON,
      position: AdobeAnalyticsConst.POSITIONS.NAVIGATION_BAR,
      name: AdobeAnalyticsConst.LINKCLICK_NAMES.FILTERS_TYPE,
    });
  }, [t, config.label, analytics]);

  return (
    <div onClick={handleOneDataLayer} className={classNames}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FilterSelect
            filterKey={filterKey}
            field={field}
            {...rest}
            isSecondaryLevelFilter={isSecondaryLevelFilter}
            useFloatingLabelProp={useFloatingLabel}
          />
        )}
      />
    </div>
  );
};

export default Filter;
